/**
 * Regelversion 7: Hohe Einkommen und Vermögen sind nicht unbegrenzt belastbar.
 *
 *  - Jedes Gesetz, das Spitzenverdiener oder Vermögende zahlen lässt (Gegenfinanzierung, Steuerbaustein,
 *    Vermögensteuer), zählt zur „Belastung der Vermögenden“ in Mrd € pro Jahr.
 *  - Abnehmender Ertrag: Bis 15 Mrd € kommt jeder Euro an, danach immer weniger (Gestaltung, Ausweichen).
 *    Ab 45 Mrd € bringt eine weitere Belastung kaum noch etwas.
 *  - Abwanderung: Liegt die Belastung über 20 Mrd €, verlagern Vermögende nach und nach Wohnsitz und
 *    Kapital ins Ausland. Das reißt Monat für Monat ein wachsendes Loch in den Haushalt.
 * Alles hier greift nur, wenn `since(7)` gilt.
 */
import { OUTLETS } from '../content/parties';
import { S, log } from './state';
import { clamp, de } from './util';
import { since } from './version';
import { press7 } from './news7';
import { lureOf } from './burden8';

export const RICH_FULL = 15, RICH_ZERO = 45, FLIGHT_FROM = 20;
/** Anteil der Kosten, den die Option der Gegenfinanzierung den Vermögenden aufbürdet (Index wie FIN_OPTS). */
const FIN_RICH = [0, 0, .7, 1];
/** Vorlagen, deren Einnahmen ganz von Vermögenden kommen. */
const RICH_TPL = ['Vermögensteuer', 'Vermögen- und Erbschaftsteuer'];

export const richOn = (): boolean => since(7);

/** Grenzertrag bei einer Belastung von x Mrd €/Jahr (1 = jeder Euro kommt an). */
export const richMarg = (x: number): number => x <= RICH_FULL ? 1 : x >= RICH_ZERO ? .1 : 1 - .9 * (x - RICH_FULL) / (RICH_ZERO - RICH_FULL);
/** Durchschnittlicher Ertrag einer zusätzlichen Belastung r ab der Belastung l0. */
export function richYield(l0: number, r: number): number {
  if (r <= 0) return 1;
  const n = 20; let s = 0;
  for (let i = 0; i < n; i++) s += richMarg(l0 + r * (i + .5) / n);
  return s / n;
}

/** Wie stark belastet ein Entwurf die Vermögenden (Mrd €/Jahr)? */
export function richOf(T: any, p: any, fx: any): number {
  let r = 0;
  if (fx?.fin?.opt) r += (fx.fin.c || 0) * (FIN_RICH[fx.fin.opt] || 0);
  if (T?.custom) for (const B of (p?.blocks || [])) if (B.k === 'tax' && (B.who || 0) === 2) r += +B.v || 0;
  if (RICH_TPL.includes(T?.t)) r += Math.max(0, fx?.b || 0);
  if (fx?.b8) r += fx.b8.rich || 0; // Version 8: Steuerreform und Standortpaket, auch Entlastung
  return Math.round(r * 10) / 10;
}

const counts = (L: any) => !L.rep && !['failed', 'blocked', 'repealed'].includes(L.stage);
/** Belastung durch Gesetze in Kraft und im Verfahren. `except`: Gesetz, das gerade überarbeitet wird. */
export function richLoad(except?: number | null, enactedOnly = false): number {
  return (S.laws || []).filter((L: any) => L.id !== except && counts(L) && (!enactedOnly || ['kraft', 'done'].includes(L.stage)))
    .reduce((s: number, L: any) => s + (L.fx?.rich || 0), 0);
}

/** Wirkung eines Entwurfs: Einnahmen von Vermögenden nach abnehmendem Ertrag. Aufruf aus lawFx. */
export function applyRich(T: any, p: any, fx: any, except?: number | null): any {
  if (!richOn()) return fx;
  const rich = richOf(T, p, fx); if (rich === 0) return fx;
  if (rich < 0) return { ...fx, rich }; // Entlastung: senkt die Belastung, kein Ertragsverlust
  const y = richYield(richLoad(except), rich), lost = rich * (1 - y);
  return { ...fx, b: (fx.b || 0) - lost, rich, richY: Math.round(y * 100) / 100 };
}

/** Monatlich: Abwanderung, wenn die Belastung dauerhaft hoch ist. */
export function richTick() {
  if (!richOn() || S.phase !== 'govern') return;
  const load = richLoad(null, true), before = S.flight || 0;
  if (load > FLIGHT_FROM) S.flight = Math.min(before + (load - FLIGHT_FROM) * .006, (load - FLIGHT_FROM) * .4);
  else S.flight = before * .97;
  if (since(8)) S.flight = (S.flight || 0) * (1 - .05 * lureOf('rich'));
  const f = S.flight || 0;
  for (const th of [1, 3, 6]) if (before < th && f >= th) {
    log(`Abwanderung: Vermögende verlagern Wohnsitz und Kapital ins Ausland. Dem Haushalt fehlen inzwischen ${de(f, 1)} Mrd € pro Jahr.`, 'bad');
    press7(OUTLETS[1].n, th >= 6 ? 'Exodus der Reichen: Steuerberater melden Rekordnachfrage nach Wegzug' : 'Immer mehr Vermögende verlassen Deutschland', th >= 6 ? -1.2 : -.6);
  }
}

/** Für die Anzeige im Haushalt. */
export function richInfo() {
  const load = richLoad(), enacted = richLoad(null, true);
  return { load, enacted, marg: richMarg(load), flight: S.flight || 0, room: Math.max(0, RICH_FULL - load) };
}
export const flightCost = (): number => (richOn() ? clamp(S.flight || 0, 0, 30) : 0);
